import { osnovaApi } from '../base';
import type {
  ChronumCostCenter,
  ChronumHours,
  ChronumLaborCost,
  ChronumLoadDetails,
  ChronumLoadsPage,
  ChronumMapRevision,
  ChronumPositionBody,
  ChronumPositionMapRow,
  ChronumPositionsResponse,
  ChronumRefreshAccepted,
  ChronumSiteBody,
  ChronumSiteMapRow,
  ChronumSitesResponse,
  ChronumStatus,
} from '../types';

/**
 * Хронум для Экономиста (`/api/v1/agents/econ/chronum`, ТЗ AI-Economist-2026
 * ред. 2.1, п. 6.4, 7.4). Читать — право чтения Экономиста; сопоставления и
 * «Обновить» — право записи. Ключ Хронума живёт только на сервере.
 *
 * Теги `EconChronum`: 'STATUS' — подключение, последние снимки и идущие
 * загрузки; 'POSITIONS' и 'SITES' — сопоставления; 'HOURS' — свод любого
 * месяца (он зависит от сопоставлений); 'SNAPSHOTS' — история загрузок;
 * 'COST_CENTERS' — используемые коды; 'LOAD:<id>' — ход одной загрузки;
 * 'HISTORY:<position|site>:<ключ>' — журнал правок сопоставления.
 */

// `as const` — литерал 'EconChronum' вместо string: иначе RTK не узнает тег из tagTypes.
const STATUS = { type: 'EconChronum', id: 'STATUS' } as const;
const POSITIONS = { type: 'EconChronum', id: 'POSITIONS' } as const;
const SITES = { type: 'EconChronum', id: 'SITES' } as const;
const HOURS = { type: 'EconChronum', id: 'HOURS' } as const;
const PAYROLL = { type: 'EconChronum', id: 'PAYROLL' } as const;
const SNAPSHOTS = { type: 'EconChronum', id: 'SNAPSHOTS' } as const;
const COST_CENTERS = { type: 'EconChronum', id: 'COST_CENTERS' } as const;

const history = (entity: 'position' | 'site', key: string) =>
  ({ type: 'EconChronum', id: `HISTORY:${entity}:${key}` }) as const;

/** Загрузок на странице истории. */
export const CHRONUM_LOADS_PAGE = 10;

const BASE = '/agents/econ/chronum';

export const econChronumApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /**
     * Есть ли ключ на сервере, последний снимок каждой ручки (ручки с месяцем —
     * за `period`), идущие загрузки и последняя загрузка месяца.
     */
    getEconChronumStatus: b.query<ChronumStatus, string>({
      query: (period) => ({ url: `${BASE}/status`, params: { period } }),
      providesTags: [STATUS],
    }),

    /** Должности Хронума с категорией ФОТ, комментарием и группой механизаторов. */
    listEconChronumPositions: b.query<ChronumPositionsResponse, void>({
      query: () => `${BASE}/positions`,
      providesTags: [POSITIONS],
    }),

    /**
     * Сопоставление должности: категория ФОТ и/или комментарий с версией
     * (`expected_updated_at`). Поля нет — не трогать. Экран сам кладёт выбор в
     * кеш списка — ответ сервера список не перечитывает, чтобы не вернуть
     * старое значение поверх нового выбора. Чужая правка — 409 `stale_version`.
     */
    setEconChronumPosition: b.mutation<
      { position: ChronumPositionMapRow | null },
      { key: string; body: ChronumPositionBody }
    >({
      query: ({ key, body }) => ({
        url: `${BASE}/positions/${encodeURIComponent(key)}`,
        method: 'PUT',
        body,
      }),
      // Только после записи: при 409 человек сам жмёт «Перечитать», данные под
      // рукой молча не меняются.
      invalidatesTags: (_r, error, { key }) =>
        error ? [] : [HOURS, PAYROLL, history('position', key)],
    }),

    /** Журнал правок сопоставления должности, свежие сверху. */
    getEconChronumPositionHistory: b.query<{ items: ChronumMapRevision[] }, string>({
      query: (key) => `${BASE}/positions/${encodeURIComponent(key)}/history`,
      providesTags: (_r, _e, key) => [history('position', key)],
    }),

    /** Площадки с привязкой: стройка (у Руководителя) или центр затрат. */
    listEconChronumSites: b.query<ChronumSitesResponse, void>({
      query: () => `${BASE}/sites`,
      providesTags: [SITES],
    }),

    /**
     * Центр затрат и/или комментарий площадки с версией. Площадке стройки центр
     * затрат не ставится — 409 с объяснением, где привязка.
     */
    setEconChronumSite: b.mutation<
      { site: ChronumSiteMapRow | null },
      { siteId: string; body: ChronumSiteBody }
    >({
      query: ({ siteId, body }) => ({
        url: `${BASE}/sites/${encodeURIComponent(siteId)}`,
        method: 'PUT',
        body,
      }),
      // Только после записи — как у должности.
      invalidatesTags: (_r, error, { siteId }) =>
        error ? [] : [SITES, HOURS, PAYROLL, COST_CENTERS, history('site', siteId)],
    }),

    /** Журнал правок привязки площадки, свежие сверху. */
    getEconChronumSiteHistory: b.query<{ items: ChronumMapRevision[] }, string>({
      query: (siteId) => `${BASE}/sites/${encodeURIComponent(siteId)}/history`,
      providesTags: (_r, _e, siteId) => [history('site', siteId)],
    }),

    /** Уже используемые коды центров затрат — для подсказки. */
    listEconChronumCostCenters: b.query<{ items: ChronumCostCenter[] }, void>({
      query: () => `${BASE}/cost-centers`,
      providesTags: [COST_CENTERS],
    }),

    /**
     * «Обновить»: начать загрузку месяца (`ГГГГ-ММ`). Ответ сразу — 202 с
     * загрузкой; сама загрузка идёт на сервере, её ход — `getEconChronumLoad`.
     * 409 `load_running` — за месяц уже идёт загрузка (она в теле ответа);
     * 409 — нет ключа Экономиста; 503 — ОСНОВА вот-вот перезапустится.
     */
    refreshEconChronum: b.mutation<ChronumRefreshAccepted, { period: string }>({
      query: (body) => ({ url: `${BASE}/refresh`, method: 'POST', body }),
      invalidatesTags: [STATUS, SNAPSHOTS],
    }),

    /** Ход загрузки и итог по каждой ручке. */
    getEconChronumLoad: b.query<ChronumLoadDetails, string>({
      query: (loadId) => `${BASE}/loads/${encodeURIComponent(loadId)}`,
      providesTags: (_r, _e, loadId) => [{ type: 'EconChronum', id: `LOAD:${loadId}` }],
    }),

    /** История загрузок месяца: загрузки со своими снимками, свежие сверху, страницами. */
    listEconChronumLoads: b.infiniteQuery<ChronumLoadsPage, string, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (last) =>
          last.offset + last.items.length < last.total ? last.offset + last.limit : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${BASE}/snapshots`,
        params: { period: queryArg, offset: pageParam, limit: CHRONUM_LOADS_PAGE },
      }),
      providesTags: [SNAPSHOTS],
    }),

    /** Свод часов месяца по объектам и центрам затрат с аномалиями. */
    getEconChronumHours: b.query<ChronumHours, string>({
      query: (period) => ({ url: `${BASE}/hours`, params: { period } }),
      providesTags: [HOURS],
    }),

    /**
     * Свод начисленного месяца (ФОТ Хронума) по объектам и центрам затрат.
     * Только с правом на ФОТ `econ.payroll.read` — иначе 403.
     */
    getEconChronumLaborCost: b.query<ChronumLaborCost, string>({
      query: (period) => ({ url: `${BASE}/labor-cost`, params: { period } }),
      providesTags: [PAYROLL],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEconChronumStatusQuery,
  useListEconChronumPositionsQuery,
  useSetEconChronumPositionMutation,
  useGetEconChronumPositionHistoryQuery,
  useListEconChronumSitesQuery,
  useSetEconChronumSiteMutation,
  useGetEconChronumSiteHistoryQuery,
  useListEconChronumCostCentersQuery,
  useRefreshEconChronumMutation,
  useGetEconChronumLoadQuery,
  useListEconChronumLoadsInfiniteQuery,
  useGetEconChronumHoursQuery,
  useGetEconChronumLaborCostQuery,
} = econChronumApi;
